import type { ReactElement } from 'react';
import { cn } from '@/lib/utils';
import { riskScoreBarClass } from './alert-detail-cards.helpers';

interface RiskScoreBarProps {
  /** 0–100 risk score, or null/undefined when not computed. */
  score?: number | null;
  className?: string;
}

/**
 * KIR-4481 — the facts-strip "RISK SCORE" value: the number plus a small bar
 * whose fill proportion is the score out of 100. Renders "—" when there is no
 * score, so the cell never implies a zero risk it did not measure.
 */
export function RiskScoreBar({
  score,
  className,
}: RiskScoreBarProps): ReactElement {
  if (score == null) {
    return <span className="text-muted-foreground">—</span>;
  }
  // Clamp the printed number too, not just the bar — a score outside 0–100
  // otherwise reads as a value the bar (pinned at 0/100%) contradicts.
  const clamped = Math.max(0, Math.min(100, score));
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span className="font-medium tabular-nums">{clamped}</span>
      <div
        className="h-1.5 w-16 overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Risk score"
      >
        <div
          className={cn('h-full rounded-full', riskScoreBarClass(clamped))}
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  );
}

RiskScoreBar.displayName = 'RiskScoreBar';
